import {
  Plus,
  MapPin,
  Calendar,
  DollarSign,
  TrendingUp,
  Eye,
  Edit,
  Settings,
  Activity,
  AlertCircle,
  BarChart3,
  Users,
  Clock,
  Wrench,
  Building,
} from "lucide-react";
import type React from "react";
import { useState, useEffect } from "react";

import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../components/ui/tabs";
import { useAuth } from "../../../hooks/useAuth";
import {
  useGetManagerAnalyticsQuery,
  useGetGroundStatsQuery,
  useGetBookingStatsQuery,
  useGetRevenueStatsQuery,
  useGetGroundUtilizationStatsQuery,
  useGetGroundRevenueStatsQuery,
  useGetRecentActivityQuery,
  useTrackEventMutation,
} from "../../../store/api/analyticsApi";
import { useGetGroundsByManagerQuery } from "../../../store/api/groundApi";
import { formatCurrency, capitalizeFirst, formatDateTime } from "../../../utils/helpers";

const ManagerDashboard: React.FC = () => {
  const { user } = useAuth();
  const [selectedTab, setSelectedTab] = useState("overview");

  // Fetch analytics data
  const { data: managerAnalyticsData, isLoading: managerAnalyticsLoading } =
    useGetManagerAnalyticsQuery();
  const { data: groundStatsData, isLoading: groundStatsLoading } = useGetGroundStatsQuery({});
  const { data: bookingStatsData } = useGetBookingStatsQuery({});
  const { data: revenueStatsData } = useGetRevenueStatsQuery({});
  const { data: groundUtilizationData } = useGetGroundUtilizationStatsQuery({});
  const { data: groundRevenueData } = useGetGroundRevenueStatsQuery({});
  const { data: recentActivityData } = useGetRecentActivityQuery({ limit: 10 });
  const { data: groundsData } = useGetGroundsByManagerQuery(user?.id || "");

  // Track events mutation
  const [trackEvent] = useTrackEventMutation();

  const managerAnalytics = managerAnalyticsData?.data;
  const groundStats = groundStatsData?.data;
  const bookingStats = bookingStatsData?.data;
  const revenueStats = revenueStatsData?.data;
  const groundUtilization = groundUtilizationData?.data;
  const groundRevenue = groundRevenueData?.data;
  const recentActivities = recentActivityData?.data || [];
  const grounds = groundsData?.data || [];

  // Track dashboard view
  useEffect(() => {
    if (user?.id) {
      trackEvent({
        type: "system_health",
        metadata: { userRole: "manager", action: "dashboard_viewed", timestamp: new Date() },
      });
    }
  }, [user?.id, trackEvent]);

  if (managerAnalyticsLoading || groundStatsLoading) {
    return (
      <div className='container py-8'>
        <div className='animate-pulse'>
          <div className='mb-4 h-8 w-1/3 rounded bg-gray-200'></div>
          <div className='mb-8 grid grid-cols-1 gap-6 md:grid-cols-4'>
            {[...Array(4)].map((_, i) => (
              <div key={i} className='h-24 rounded bg-gray-200'></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='container py-8'>
      <div className='mb-8 flex items-center justify-between'>
        <div>
          <h1 className='mb-2 text-3xl font-bold'>Manager Dashboard</h1>
          <p className='text-muted-foreground'>Manage your grounds and track performance.</p>
        </div>
        <Button className='flex items-center gap-2'>
          <Plus className='h-4 w-4' />
          Add Ground
        </Button>
      </div>

      {/* Stats Cards */}
      <div className='mb-8 grid grid-cols-1 gap-6 md:grid-cols-4'>
        <Card>
          <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
            <CardTitle className='text-sm font-medium'>Total Grounds</CardTitle>
            <MapPin className='h-4 w-4 text-muted-foreground' />
          </CardHeader>
          <CardContent>
            <div className='text-2xl font-bold'>{groundStats?.totalGrounds || 0}</div>
            <p className='text-xs text-muted-foreground'>
              {groundStats?.activeGrounds || 0} active
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
            <CardTitle className='text-sm font-medium'>Total Bookings</CardTitle>
            <Calendar className='h-4 w-4 text-muted-foreground' />
          </CardHeader>
          <CardContent>
            <div className='text-2xl font-bold'>{bookingStats?.totalBookings || 0}</div>
            <p className='text-xs text-muted-foreground'>
              {bookingStats?.confirmedBookings || 0} confirmed
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
            <CardTitle className='text-sm font-medium'>Monthly Revenue</CardTitle>
            <DollarSign className='h-4 w-4 text-muted-foreground' />
          </CardHeader>
          <CardContent>
            <div className='text-2xl font-bold'>
              {formatCurrency(revenueStats?.monthlyRevenue || 0)}
            </div>
            <p className='text-xs text-muted-foreground'>
              +{revenueStats?.revenueGrowth?.toFixed(1) || 0}% from last month
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
            <CardTitle className='text-sm font-medium'>Utilization Rate</CardTitle>
            <TrendingUp className='h-4 w-4 text-muted-foreground' />
          </CardHeader>
          <CardContent>
            <div className='text-2xl font-bold'>
              {groundStats?.utilizationRate?.toFixed(1) || 0}%
            </div>
            <p className='text-xs text-muted-foreground'>
              Avg: {formatCurrency(bookingStats?.averageBookingValue || 0)} per booking
            </p>
          </CardContent>
        </Card>
      </div>

      <Tabs value={selectedTab} onValueChange={setSelectedTab} className='space-y-6'>
        <TabsList>
          <TabsTrigger value='overview'>Overview</TabsTrigger>
          <TabsTrigger value='grounds'>My Grounds</TabsTrigger>
          <TabsTrigger value='bookings'>Bookings</TabsTrigger>
          <TabsTrigger value='analytics'>Analytics</TabsTrigger>
          <TabsTrigger value='maintenance'>Maintenance</TabsTrigger>
        </TabsList>

        <TabsContent value='overview' className='space-y-6'>
          <div className='grid grid-cols-1 gap-6 lg:grid-cols-2'>
            {/* Recent Activity Card */}
            <Card>
              <CardHeader>
                <CardTitle className='flex items-center gap-2'>
                  <Activity className='h-5 w-5' />
                  Recent Activity
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className='space-y-4'>
                  {recentActivities.length > 0 ? (
                    recentActivities.slice(0, 5).map((activity: any, index: number) => (
                      <div key={index} className='flex items-center justify-between'>
                        <div>
                          <h4 className='font-medium'>
                            {capitalizeFirst(activity.type.replace(/_/g, " "))}
                          </h4>
                          <p className='text-sm text-muted-foreground'>
                            {formatDateTime(activity.timestamp)}
                          </p>
                        </div>
                        <Badge
                          variant={activity.type.includes("booking") ? "default" : "secondary"}
                        >
                          {activity.type.includes("error") ? "Error" : "Success"}
                        </Badge>
                      </div>
                    ))
                  ) : (
                    <div className='py-4 text-center text-muted-foreground'>
                      No recent activities
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Quick Actions Card */}
            <Card>
              <CardHeader>
                <CardTitle>Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className='space-y-3'>
                <Button className='w-full justify-start bg-transparent' variant='outline'>
                  <Plus className='mr-2 h-4 w-4' />
                  Add New Ground
                </Button>
                <Button className='w-full justify-start bg-transparent' variant='outline'>
                  <Calendar className='mr-2 h-4 w-4' />
                  View Bookings
                </Button>
                <Button className='w-full justify-start bg-transparent' variant='outline'>
                  <Settings className='mr-2 h-4 w-4' />
                  Ground Settings
                </Button>
                <Button className='w-full justify-start bg-transparent' variant='outline'>
                  <BarChart3 className='mr-2 h-4 w-4' />
                  View Analytics
                </Button>
              </CardContent>
            </Card>

            {/* Ground Revenue Analytics */}
            {managerAnalytics?.revenueByGround && (
              <Card className='lg:col-span-2'>
                <CardHeader>
                  <CardTitle>Ground Performance</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className='space-y-4'>
                    {managerAnalytics.revenueByGround
                      .slice(0, 5)
                      .map((ground: any, index: number) => (
                        <div key={index} className='flex items-center justify-between'>
                          <div>
                            <h4 className='font-medium'>{ground.groundName}</h4>
                            <p className='text-sm text-muted-foreground'>
                              {ground.bookingCount} bookings • {ground.utilizationRate?.toFixed(1)}%
                              utilized
                            </p>
                          </div>
                          <div className='text-right'>
                            <div className='font-medium text-green-600'>
                              {formatCurrency(ground.totalRevenue)}
                            </div>
                            <Badge variant='default'>Top Performer</Badge>
                          </div>
                        </div>
                      ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value='grounds' className='space-y-4'>
          <Card>
            <CardHeader>
              <CardTitle>My Grounds ({grounds.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {grounds.length > 0 ? (
                <div className='space-y-4'>
                  {grounds.map(ground => (
                    <div
                      key={ground.id}
                      className='flex items-center justify-between rounded-lg border p-4 transition-colors hover:bg-gray-50'
                    >
                      <div className='flex-1'>
                        <h4 className='font-medium'>{ground.name}</h4>
                        <div className='mt-1 flex items-center gap-4 text-sm text-muted-foreground'>
                          <span className='flex items-center gap-1'>
                            <Building className='h-3 w-3' />
                            {ground.type.join(", ")}
                          </span>
                          <span className='flex items-center gap-1'>
                            <MapPin className='h-3 w-3' />
                            {ground.location.city}
                          </span>
                          <span className='flex items-center gap-1'>
                            <Users className='h-3 w-3' />
                            {ground.capacity.toLocaleString()} capacity
                          </span>
                        </div>
                        <div className='mt-2 flex gap-1'>
                          {ground.specs.slice(0, 3).map((spec: string) => (
                            <Badge key={spec} variant='secondary' className='text-xs'>
                              {capitalizeFirst(spec)}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      <div className='flex items-center gap-2'>
                        <div className='mr-4 text-right'>
                          <div className='font-medium'>
                            {formatCurrency(ground.pricing.basePrice)}/hr
                          </div>
                          <Badge variant={ground.status === "active" ? "default" : "secondary"}>
                            {capitalizeFirst(ground.status)}
                          </Badge>
                        </div>
                        <Button size='sm' variant='outline'>
                          <Eye className='h-4 w-4' />
                        </Button>
                        <Button size='sm' variant='outline'>
                          <Edit className='h-4 w-4' />
                        </Button>
                        <Button size='sm' variant='outline'>
                          <Settings className='h-4 w-4' />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className='py-8 text-center'>
                  <MapPin className='mx-auto mb-4 h-12 w-12 text-muted-foreground' />
                  <p className='mb-4 text-muted-foreground'>No grounds added yet</p>
                  <Button>
                    <Plus className='mr-2 h-4 w-4' />
                    Add Your First Ground
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Ground Statistics */}
          {groundStats && (
            <div className='grid grid-cols-1 gap-6 md:grid-cols-3'>
              <Card>
                <CardHeader>
                  <CardTitle className='text-lg'>Ground Status</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className='space-y-3'>
                    <div className='flex items-center justify-between'>
                      <span>Active:</span>
                      <Badge variant='default'>{groundStats.activeGrounds}</Badge>
                    </div>
                    <div className='flex items-center justify-between'>
                      <span>Inactive:</span>
                      <Badge variant='secondary'>{groundStats.inactiveGrounds}</Badge>
                    </div>
                    <div className='flex items-center justify-between'>
                      <span>Maintenance:</span>
                      <Badge variant='destructive'>{groundStats.maintenanceGrounds}</Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className='text-lg'>Capacity Stats</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className='space-y-3'>
                    <div className='flex justify-between'>
                      <span>Total Capacity:</span>
                      <span className='font-medium'>
                        {groundStats.totalCapacity?.toLocaleString()}
                      </span>
                    </div>
                    <div className='flex justify-between'>
                      <span>Average:</span>
                      <span className='font-medium'>
                        {Math.round(groundStats.averageCapacity || 0)}
                      </span>
                    </div>
                    <div className='flex justify-between'>
                      <span>Utilization:</span>
                      <span className='font-medium text-green-600'>
                        {groundStats.utilizationRate?.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className='text-lg'>Ground Types</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className='space-y-2'>
                    {Object.entries(groundStats.groundsByType || {}).map(([type, count]) => (
                      <div key={type} className='flex justify-between'>
                        <span>{capitalizeFirst(type.replace(/-/g, " "))}:</span>
                        <span className='font-medium'>{count}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        <TabsContent value='bookings' className='space-y-4'>
          <Card>
            <CardHeader>
              <CardTitle>Booking Statistics</CardTitle>
            </CardHeader>
            <CardContent>
              {bookingStats ? (
                <div className='grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4'>
                  <div className='text-center'>
                    <div className='text-2xl font-bold text-blue-600'>
                      {bookingStats.totalBookings}
                    </div>
                    <p className='text-sm text-muted-foreground'>Total Bookings</p>
                  </div>
                  <div className='text-center'>
                    <div className='text-2xl font-bold text-green-600'>
                      {bookingStats.confirmedBookings}
                    </div>
                    <p className='text-sm text-muted-foreground'>Confirmed</p>
                  </div>
                  <div className='text-center'>
                    <div className='text-2xl font-bold text-yellow-600'>
                      {bookingStats.pendingBookings}
                    </div>
                    <p className='text-sm text-muted-foreground'>Pending</p>
                  </div>
                  <div className='text-center'>
                    <div className='text-2xl font-bold text-red-600'>
                      {bookingStats.cancelledBookings}
                    </div>
                    <p className='text-sm text-muted-foreground'>Cancelled</p>
                  </div>
                </div>
              ) : (
                <div className='py-8 text-center'>
                  <Calendar className='mx-auto mb-4 h-12 w-12 text-muted-foreground' />
                  <p className='text-muted-foreground'>Loading booking statistics...</p>
                </div>
              )}
            </CardContent>
          </Card>

          {bookingStats?.bookingsByMonth && (
            <Card>
              <CardHeader>
                <CardTitle>Monthly Booking Trends</CardTitle>
              </CardHeader>
              <CardContent>
                <div className='space-y-3'>
                  {bookingStats.bookingsByMonth.slice(-6).map((month: any) => (
                    <div key={month.month} className='flex items-center justify-between'>
                      <span className='font-medium'>{month.month}</span>
                      <div className='flex items-center gap-4'>
                        <span className='text-sm text-muted-foreground'>
                          {month.count} bookings
                        </span>
                        <span className='font-medium text-green-600'>
                          {formatCurrency(month.revenue)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value='analytics' className='space-y-4'>
          <div className='grid grid-cols-1 gap-6 md:grid-cols-2'>
            <Card>
              <CardHeader>
                <CardTitle>Revenue Analytics</CardTitle>
              </CardHeader>
              <CardContent>
                {revenueStats ? (
                  <div className='space-y-4'>
                    <div className='flex items-center justify-between'>
                      <span>Total Revenue:</span>
                      <span className='text-lg font-bold text-green-600'>
                        {formatCurrency(revenueStats.totalRevenue)}
                      </span>
                    </div>
                    <div className='flex items-center justify-between'>
                      <span>Monthly Revenue:</span>
                      <span className='font-medium'>
                        {formatCurrency(revenueStats.monthlyRevenue)}
                      </span>
                    </div>
                    <div className='flex items-center justify-between'>
                      <span>Growth Rate:</span>
                      <span
                        className={`font-medium ${revenueStats.revenueGrowth >= 0 ? "text-green-600" : "text-red-600"}`}
                      >
                        {revenueStats.revenueGrowth > 0 ? "+" : ""}
                        {revenueStats.revenueGrowth?.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className='py-4 text-center text-muted-foreground'>
                    Loading revenue data...
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Performance Metrics</CardTitle>
              </CardHeader>
              <CardContent>
                <div className='space-y-4'>
                  <div className='flex items-center justify-between'>
                    <span>Conversion Rate:</span>
                    <span className='font-medium text-blue-600'>
                      {bookingStats?.conversionRate?.toFixed(1) || 0}%
                    </span>
                  </div>
                  <div className='flex items-center justify-between'>
                    <span>Cancellation Rate:</span>
                    <span className='font-medium text-orange-600'>
                      {bookingStats?.cancellationRate?.toFixed(1) || 0}%
                    </span>
                  </div>
                  <div className='flex items-center justify-between'>
                    <span>Avg Booking Value:</span>
                    <span className='font-medium'>
                      {formatCurrency(bookingStats?.averageBookingValue || 0)}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {managerAnalytics?.bookingPatterns && (
            <Card>
              <CardHeader>
                <CardTitle>Booking Patterns</CardTitle>
              </CardHeader>
              <CardContent>
                <div className='space-y-3'>
                  {managerAnalytics.bookingPatterns.slice(-7).map((pattern: any, index: number) => (
                    <div key={index} className='flex items-center justify-between'>
                      <span className='text-sm font-medium'>{pattern.date}</span>
                      <div className='flex items-center gap-4'>
                        <span className='text-sm text-muted-foreground'>
                          {pattern.bookings} bookings
                        </span>
                        <span className='text-sm font-medium'>
                          {formatCurrency(pattern.revenue)}
                        </span>
                        <span className='text-xs text-muted-foreground'>
                          Avg: {formatCurrency(pattern.averageBookingValue)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value='maintenance' className='space-y-4'>
          <Card>
            <CardHeader>
              <CardTitle className='flex items-center gap-2'>
                <Wrench className='h-5 w-5' />
                Maintenance Schedule
              </CardTitle>
            </CardHeader>
            <CardContent>
              {managerAnalytics?.maintenanceSchedule &&
              managerAnalytics.maintenanceSchedule.length > 0 ? (
                <div className='space-y-4'>
                  {managerAnalytics.maintenanceSchedule.map((maintenance: any, index: number) => (
                    <div
                      key={index}
                      className='flex items-center justify-between rounded-lg border p-4'
                    >
                      <div>
                        <h4 className='font-medium'>{maintenance.groundName}</h4>
                        <p className='text-sm text-muted-foreground'>
                          Next maintenance: {formatDateTime(maintenance.nextMaintenance)}
                        </p>
                      </div>
                      <div className='flex items-center gap-2'>
                        <Badge variant='outline' className='flex items-center gap-1'>
                          <Clock className='h-3 w-3' />
                          Scheduled
                        </Badge>
                        <Button size='sm' variant='outline'>
                          <Settings className='h-4 w-4' />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className='py-8 text-center'>
                  <Wrench className='mx-auto mb-4 h-12 w-12 text-muted-foreground' />
                  <p className='mb-4 text-muted-foreground'>No maintenance scheduled</p>
                  <Button variant='outline'>
                    <Calendar className='mr-2 h-4 w-4' />
                    Schedule Maintenance
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Maintenance alerts or warnings */}
          <Card>
            <CardHeader>
              <CardTitle className='flex items-center gap-2'>
                <AlertCircle className='h-5 w-5' />
                Maintenance Alerts
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className='space-y-3'>
                {grounds.filter(g => g.status === "maintenance").length > 0 && (
                  <div className='flex items-center gap-3 rounded-lg border border-orange-200 bg-orange-50 p-3'>
                    <AlertCircle className='h-5 w-5 text-orange-600' />
                    <div>
                      <h4 className='font-medium text-orange-800'>Grounds under maintenance</h4>
                      <p className='text-sm text-orange-600'>
                        {grounds.filter(g => g.status === "maintenance").length} ground(s) currently
                        unavailable
                      </p>
                    </div>
                  </div>
                )}

                {grounds.filter(g => g.status === "active").length === 0 && (
                  <div className='flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 p-3'>
                    <AlertCircle className='h-5 w-5 text-red-600' />
                    <div>
                      <h4 className='font-medium text-red-800'>No active grounds</h4>
                      <p className='text-sm text-red-600'>
                        All grounds are inactive or under maintenance
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ManagerDashboard;
